import { useEffect, useState } from 'react';
import { FileText, Download, Music, Video } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// Cache global de imagens carregadas nesta sessão (não sobrevive a reload da página)
const imageCache = new Map(); // id -> blobUrl

function isImage(mime) {
  return mime && mime.startsWith('image/');
}
function isAudio(mime) {
  return mime && mime.startsWith('audio/');
}
function isVideo(mime) {
  return mime && mime.startsWith('video/');
}
function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AuthedImage({ id, alt, filename, size, onClick, onLoad }) {
  const [src, setSrc] = useState(() => imageCache.get(id) || null);
  useEffect(() => {
    if (imageCache.has(id)) {
      onLoad?.();
      return;
    }
    let aborted = false;
    const token = localStorage.getItem('token');
    fetch(`${API}/api/v1/importer/assets/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (aborted) return;
        if (!blob) { onLoad?.(); return; }
        const url = URL.createObjectURL(blob);
        imageCache.set(id, url);
        setSrc(url);
        onLoad?.();
      })
      .catch(() => { onLoad?.(); });
    return () => { aborted = true; };
  }, [id]);
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      onClick={() => onClick?.(src)}
      loading="eager"
      decoding="async"
      className="max-w-sm max-h-64 rounded border border-border/50 object-contain cursor-zoom-in hover:border-accent/50 transition-colors"
    />
  );
}

async function downloadAsset(id, filename) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}/api/v1/importer/assets/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `asset-${id}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function CommentAttachments({ assets = [], onImageReady }) {
  const [lightbox, setLightbox] = useState(null); // { src, alt } ou null

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  if (!assets || assets.length === 0) return null;
  return (
    <>
    <div className="mt-2 flex flex-wrap gap-2">
      {assets.map((a) => {
        if (isImage(a.mime_type)) {
          return (
            <div key={a.id} className="group/att relative">
              <AuthedImage
                id={a.id}
                alt={a.filename}
                filename={a.filename}
                size={a.size_bytes}
                onClick={(src) => setLightbox({ src, alt: a.filename })}
                onLoad={onImageReady}
              />
              <button
                onClick={() => downloadAsset(a.id, a.filename)}
                className="absolute top-1 right-1 p-1 rounded bg-black/60 text-white opacity-0 group-hover/att:opacity-100 transition-opacity"
                title="Baixar"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        }
        if (isAudio(a.mime_type)) {
          return (
            <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-main/50 border border-border/50 rounded">
              <Music className="w-4 h-4 text-text-secondary shrink-0" />
              <button
                onClick={() => downloadAsset(a.id, a.filename)}
                className="text-xs text-text-primary hover:text-accent transition-colors truncate max-w-[200px]"
                title={a.filename}
              >
                {a.filename}
              </button>
              <span className="text-[10px] text-text-secondary shrink-0">{fmtSize(a.size_bytes)}</span>
            </div>
          );
        }
        if (isVideo(a.mime_type)) {
          return (
            <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-main/50 border border-border/50 rounded">
              <Video className="w-4 h-4 text-text-secondary shrink-0" />
              <button
                onClick={() => downloadAsset(a.id, a.filename)}
                className="text-xs text-text-primary hover:text-accent transition-colors truncate max-w-[200px]"
                title={a.filename}
              >
                {a.filename}
              </button>
              <span className="text-[10px] text-text-secondary shrink-0">{fmtSize(a.size_bytes)}</span>
            </div>
          );
        }
        // generico (pdf, docx, etc)
        return (
          <button
            key={a.id}
            onClick={() => downloadAsset(a.id, a.filename)}
            className="flex items-center gap-2 px-3 py-2 bg-main/50 border border-border/50 rounded hover:border-accent/50 transition-colors"
            title={a.filename}
          >
            <FileText className="w-4 h-4 text-text-secondary shrink-0" />
            <span className="text-xs text-text-primary truncate max-w-[200px]">{a.filename}</span>
            <span className="text-[10px] text-text-secondary shrink-0">{fmtSize(a.size_bytes)}</span>
            <Download className="w-3.5 h-3.5 text-text-secondary" />
          </button>
        );
      })}
    </div>
    {lightbox && (
      <div
        onClick={() => setLightbox(null)}
        className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
      >
        <img
          src={lightbox.src}
          alt={lightbox.alt}
          onClick={(e) => e.stopPropagation()}
          className="max-w-[95vw] max-h-[95vh] object-contain rounded shadow-2xl cursor-default"
        />
      </div>
    )}
    </>
  );
}
